import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { RootState, useAppDispatch } from '../../../../store';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import alert from '../../../../utils/alert';
import Select from '../../../app/components/Select';
import { US_STATES_DROPDOWN_SHORT } from '../../../../utils/usStates';
import useCountries from '../../../../utils/countries';
import { extractDataFromPlaceDetails } from '../../../../utils/google';
import PlacesAutocomplete from '../../../../components/PlacesAutocomplete';
import type { PlaceDetails } from '../../../../api/services/PlacesService';
import { HouseholdMemberSchema } from '../../schemas/householdMemberSchema';
import './AddMemberModal.scss';
import { MINOR_AGE } from '../../../../config';
import { CommonDropdownType } from '../../../../types';
import { useSelector } from 'react-redux';
import { searchUserThunk } from '../../../register/api/registerSlice';
import {
  addHouseholdMemberThunk,
  getHouseholdMembersThunk,
  updateHouseholdMemberThunk,
} from '../../api/householdMemberSlice';
import memberDetailsFormToParams from '../../../../utils/memberDetailsFormToParams';
import { Member, MemberDetailsType } from '../../types';
import addMemberIconPath from '../../../../assets/images/icons/member-add-icon.png';
import LoaderButton from '../../../../components/common/LoaderButton';
import memberToUpdateParams from '../../../../utils/memberToUpdateParams';
import patientToUpdateParams, { IdentifierType } from '../../../../utils/patientToUpdateParams';
import { parseLocalDate } from '../../../../utils/general';

import ConfirmationModal from '../../../app/components/ConfirmationModal';
interface AddMemberModalProps {
  show: boolean;
  handleClose: () => void;
  userInfo: any;
  mode: 'add' | 'edit';
  selectedMember?: Member | null;
}

type AddMemberFormData = {
  firstName: string;
  middleName?: string;
  lastName: string;
  relationship: { label: string; value: string };
  birthdate?: string;
  sex: { label: string; value: string };
  street: string;
  unit?: string;
  city: string;
  state: { label: string; value: string };
  zip: string;
  country: { label: string; value: string };
  email?: string;
  phone?: string;
};

const AddOrEditMemberModal: React.FC<AddMemberModalProps> = ({
  show,
  handleClose,
  userInfo,
  mode,
  selectedMember,
}) => {
  const states: CommonDropdownType[] = US_STATES_DROPDOWN_SHORT;
  const countries: CommonDropdownType[] = useCountries();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const autocompleteRef = useRef<HTMLInputElement | null>(null);

  const [isAdult, setIsAdult] = useState(true);
  const [phoneAvailable, setPhoneAvailable] = useState(true);
  const [checkingPhoneAvailability, setCheckingPhoneAvailability] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [checkingEmailAvailability, setCheckingEmailAvailability] = useState(false);
  const accessToken = useSelector((state: RootState) => state?.auth?.accessToken);
  const [isChanged, setIsChanged] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    reset,
  } = useForm<AddMemberFormData>({
    mode: 'onChange',
    context: { isAdult, mode },
    resolver: yupResolver(HouseholdMemberSchema),
    defaultValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      birthdate: '',
      relationship: undefined,
      sex: undefined,
      street: '',
      unit: '',
      city: '',
      state: undefined,
      zip: '',
      country: undefined,
      email: '',
      phone: '',
    },
  });

  useEffect(() => {
    if (mode === 'edit' && selectedMember) {
      // Parse birthDate correctly to avoid timezone issues
      // If it's already a string in YYYY-MM-DD format, use it directly
      // Otherwise, parse it as a local date and format it
      let birthdateString = '';
      if (selectedMember.birthDate) {
        if (
          typeof selectedMember.birthDate === 'string' &&
          /^\d{4}-\d{2}-\d{2}$/.test(selectedMember.birthDate)
        ) {
          // Already in YYYY-MM-DD format, use directly
          birthdateString = selectedMember.birthDate;
        } else {
          // Parse as local date and format to YYYY-MM-DD
          const localDate = parseLocalDate(String(selectedMember.birthDate));
          if (!isNaN(localDate.getTime())) {
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0');
            const day = String(localDate.getDate()).padStart(2, '0');
            birthdateString = `${year}-${month}-${day}`;
          }
        }
      }
      reset({
        firstName: selectedMember.name?.[0]?.given?.[0] || '',
        middleName: selectedMember.name?.[0]?.given?.[1] || '',
        lastName: selectedMember.name?.[0]?.family || '',
        birthdate: birthdateString,
        relationship: selectedMember.relationship?.text
          ? { label: t(selectedMember.relationship.text), value: selectedMember.relationship.text }
          : undefined,
        sex: selectedMember.gender
          ? {
              label: t(
                selectedMember.gender.charAt(0).toUpperCase() + selectedMember.gender.slice(1),
              ),
              value: selectedMember.gender,
            }
          : undefined,
        street: selectedMember.address?.[0]?.line?.[0] || '',
        unit: selectedMember.address?.[0]?.line?.[1] || '',
        city: selectedMember.address?.[0]?.city || '',
        state: selectedMember.address?.[0]?.state
          ? { label: t(selectedMember.address[0].state), value: selectedMember.address[0].state }
          : undefined,
        zip: selectedMember.address?.[0]?.postalCode || '',
        country: selectedMember.address?.[0]?.country
          ? {
              label: t(selectedMember.address[0].country),
              value: selectedMember.address[0].country,
            }
          : undefined,
      });
    } else if (mode === 'add') {
      reset({
        firstName: '',
        middleName: '',
        lastName: '',
        birthdate: '',
        relationship: { label: t('Select'), value: '' },
        sex: { label: t('Select'), value: '' },
        street: '',
        unit: '',
        city: '',
        state: { label: t('Select'), value: '' },
        zip: '',
        country: { label: t('Select'), value: '' },
        email: '',
        phone: '',
      });
    }
  }, [mode, selectedMember, reset, t]);

  const watchedFields = watch();

  useEffect(() => {
    setIsChanged(isDirty);
  }, [isDirty, watchedFields]);

  const isMinor = !isAdult;

  const checkIfEmailOrPhoneAvailable = useCallback(
    (searchResult: any, value: string, type: string) => {
      const isEmail = type === 'email';
      const availabilityState = isEmail ? setEmailAvailable : setPhoneAvailable;
      const checkingState = isEmail ? setCheckingEmailAvailability : setCheckingPhoneAvailability;

      if (searchResult.entry && searchResult.entry.length > 0) {
        const item = searchResult.entry[0]?.resource?.telecom?.find(
          (item: { system: string; value: string }) => item.system === type,
        );
        availabilityState(!(item && item.value.toLowerCase() === value.toLowerCase()));
      } else {
        availabilityState(true);
      }
      checkingState(false);
    },
    [],
  );

  const searchEmailOrPhoneNumber = useCallback(
    async (value: string, type: string) => {
      const isEmail = type === 'email';
      const checkingState = isEmail ? setCheckingEmailAvailability : setCheckingPhoneAvailability;
      const availabilityState = isEmail ? setEmailAvailable : setPhoneAvailable;

      checkingState(true);

      try {
        const searchBody = {
          searchData: { telecom: value, limit: 1 },
          accessToken: accessToken || '',
        };

        const resultAction = await dispatch(searchUserThunk(searchBody));

        if (searchUserThunk.fulfilled.match(resultAction)) {
          checkIfEmailOrPhoneAvailable(resultAction.payload, value, type);
        } else {
          availabilityState(true);
        }
      } catch (error) {
        availabilityState(true);
      } finally {
        checkingState(false);
      }
    },
    [accessToken, dispatch, checkIfEmailOrPhoneAvailable],
  );

  const convertToMemberDetails = (data: AddMemberFormData): MemberDetailsType => ({
    birthdate: data.birthdate,
    firstName: data.firstName,
    middleName: data.middleName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    street: data.street,
    unit: data.unit,
    city: data.city,
    state: data.state.value,
    zip: data.zip,
    country: data.country.value,
    relationship: data.relationship.value,
    sex: data.sex.value,
  });

  const onSubmit = useCallback(
    async (data: AddMemberFormData) => {
      const memberDetails = convertToMemberDetails(data);
      if (!accessToken) {
        alert.error(t('Something went wrong. Please try again.'));
        return;
      }

      try {
        if (mode === 'edit' && selectedMember) {
          const memberParams = memberToUpdateParams(
            memberDetails,
            userInfo?.identifier[0]?.value,
            selectedMember,
          );
          const patientParams = patientToUpdateParams(
            memberDetails,
            selectedMember?.identifier as IdentifierType | undefined,
          );

          await dispatch(
            updateHouseholdMemberThunk({
              memberParams,
              patientParams,
              accessToken,
            }),
          );

          alert.success(t('Household member is edited successfully.'));
        } else {
          const inviterDisplay = `${userInfo?.name[0]?.given[0] || ''} ${
            userInfo?.name[0]?.given[1] ? userInfo?.name[0]?.given[1][0] + ' ' : ''
          }${userInfo?.name[0]?.family || ''}`;

          const params = memberDetailsFormToParams(
            memberDetails,
            isMinor,
            userInfo?.identifier[0]?.value,
            inviterDisplay,
          );
          await dispatch(
            addHouseholdMemberThunk({
              memberData: params,
              accessToken,
            }),
          );

          alert.success(
            isMinor
              ? t('Member Added Successfully: Household member is added to your list.')
              : t('Invitation Sent Successfully: Your household member has been invited.'),
          );
        }
        handleClose();
        dispatch(getHouseholdMembersThunk({ accessToken }));
      } catch (error) {
        alert.error(t('Something went wrong. Please try again.'));
      }
    },
    [dispatch, handleClose, t, accessToken, isMinor, userInfo, mode, selectedMember],
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (date && !isNaN(date.getTime())) {
        const dateString = date.toISOString().split('T')[0];
        setValue('birthdate', dateString, { shouldValidate: true, shouldDirty: true });

        const today = new Date();
        const age = today.getFullYear() - date.getFullYear();
        setIsAdult(age >= MINOR_AGE);

        if (mode === 'edit' && age < MINOR_AGE) {
          setError('birthdate', {
            type: 'manual',
            message: t('Minors may not be more than 18 years old'),
          });
        } else {
          clearErrors('birthdate');
        }
      } else {
        setValue('birthdate', '', { shouldValidate: true, shouldDirty: true });
      }
    },
    [mode, setError, clearErrors, t, setValue],
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const phoneNumber = e.target.value.replace(/[^\d]/g, '');
      setValue('phone', phoneNumber, { shouldValidate: true });
      if (phoneNumber.length >= 10) {
        searchEmailOrPhoneNumber(phoneNumber, 'phone');
      } else {
        setPhoneAvailable(true);
      }
    },
    [setValue, searchEmailOrPhoneNumber],
  );

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const email = event.target.value;
      setValue('email', email, { shouldValidate: true });
      if (email && email.includes('@')) {
        searchEmailOrPhoneNumber(email, 'email');
      } else {
        setEmailAvailable(true);
      }
    },
    [setValue, searchEmailOrPhoneNumber],
  );

  const handleCloseModal = useCallback(() => {
    if (isChanged) {
      setShowConfirmationModal(true);
    } else {
      handleClose();
    }
  }, [isChanged, handleClose]);

  const confirmCloseModal = useCallback(() => {
    setShowConfirmationModal(false);
    handleClose();
  }, [handleClose]);

  const handlePlaceSelected = useCallback(
    (place: PlaceDetails) => {
      const { city, state, zip, country, formattedAddress, streetNumber, route } =
        extractDataFromPlaceDetails(place);

      setValue('city', city?.long_name || '', { shouldValidate: true, shouldDirty: true });
      setValue('zip', zip?.long_name || '', { shouldValidate: true, shouldDirty: true });
      if (state) {
        setValue(
          'state',
          { label: state.long_name, value: state.long_name },
          { shouldValidate: true, shouldDirty: true },
        );
      }
      if (country) {
        setValue(
          'country',
          { label: country.long_name, value: country.long_name },
          { shouldValidate: true, shouldDirty: true },
        );
      }
      const streetValue = route
        ? `${streetNumber ? `${streetNumber.long_name} ` : ''}${route.long_name}`
        : formattedAddress?.split(',')[0] || '';
      setValue('street', streetValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  return (
    <>
      <Modal show={show} onHide={handleCloseModal} size='lg' className='modal-add'>
        <div className='modal__header-container'>
          <div className='icon-container'>
            <img
              src={addMemberIconPath}
              alt='Add Member'
              width='100'
              height='100'
              className='add-member-img'
            />
          </div>
          <Modal.Header closeButton className='modal-header-add'>
            <Modal.Title className='modal-header-add-title'>{t('Member Details')}</Modal.Title>
          </Modal.Header>
        </div>
        <Modal.Body className='modal-add-body'>
          <Form className='add-member-form' onSubmit={handleSubmit(onSubmit)}>
            <div className='form-row'>
              <Form.Group className='form-group' controlId='formDOB'>
                <Form.Label>{t('Date of Birth')}</Form.Label>
                <Controller
                  control={control}
                  name='birthdate'
                  render={({ field }) => {
                    let selectedDate = null;
                    if (field.value) {
                      // field.value is a string in YYYY-MM-DD format
                      // Use parseLocalDate to avoid timezone issues
                      const date = parseLocalDate(field.value);
                      if (!isNaN(date.getTime())) {
                        selectedDate = date;
                      }
                    }
                    return (
                      <div className='datepicker-wrapper'>
                        <DatePicker
                          showIcon
                          onChange={(date: Date | null) => {
                            handleDateChange(date);
                          }}
                          onBlur={field.onBlur}
                          selected={selectedDate}
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={100}
                          showMonthDropdown
                          dropdownMode='scroll'
                          maxDate={new Date()}
                          dateFormat='MM/dd/yyyy'
                          className='form-control datepicker-input'
                          calendarClassName='custom-datepicker-calendar'
                          id='update-user-birthDate'
                        />
                      </div>
                    );
                  }}
                />
                {errors.birthdate?.message && (
                  <Form.Text className='text-danger'>{t(errors.birthdate.message)}</Form.Text>
                )}
              </Form.Group>

              <Form.Group className='form-group' controlId='formRelationship'>
                <Form.Label>{t('Relationship')}</Form.Label>
                <Controller
                  name='relationship'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={[
                        { label: t('Spouse'), value: 'Spouse' },
                        { label: t('Child'), value: 'Child' },
                        { label: t('Grandchild'), value: 'Grandchild' },
                        { label: t('Niece/nephew'), value: 'Niece/nephew' },
                        { label: t('Parent'), value: 'Parent' },
                        { label: t('Grandparent'), value: 'Grandparent' },
                        { label: t('Other Dependent'), value: 'Other Dependent' },
                      ]}
                      hideSelectedOptions={false}
                      controlShouldRenderValue={true}
                      name='relationship'
                      id='relationship-select'
                      inputId='relationship-input'
                      menuPlacement='top'
                      placeholder={t('Select')}
                    />
                  )}
                />
                {(errors.relationship?.message || (errors.relationship as any)?.value?.message) && (
                  <Form.Text className='text-danger'>
                    {t(
                      (errors.relationship as any)?.value?.message || errors.relationship?.message,
                    )}
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            <div className='form-row'>
              <Form.Group className='form-group' controlId='formFirstName'>
                <Form.Label>{t('First Name')}</Form.Label>
                <Form.Control type='text' {...register('firstName')} />
                {errors.firstName?.message && (
                  <Form.Text className='text-danger'>{t(errors.firstName.message)}</Form.Text>
                )}
              </Form.Group>

              <Form.Group className='form-group' controlId='formMiddleName'>
                <Form.Label>{t('Middle Name')}</Form.Label>
                <Form.Control type='text' {...register('middleName')} />
                {errors.middleName?.message && (
                  <Form.Text className='text-danger'>{t(errors.middleName.message)}</Form.Text>
                )}
              </Form.Group>
            </div>

            <div className='form-row'>
              <Form.Group className='form-group' controlId='formLastName'>
                <Form.Label>{t('Last Name')}</Form.Label>
                <Form.Control type='text' {...register('lastName')} />
                {errors.lastName?.message && (
                  <Form.Text className='text-danger'>{t(errors.lastName.message)}</Form.Text>
                )}
              </Form.Group>

              <Form.Group className='form-group' controlId='formSex'>
                <Form.Label>{t('Sex')}</Form.Label>
                <Controller
                  name='sex'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={[
                        { value: 'male', label: t('Male') },
                        { value: 'female', label: t('Female') },
                        { value: 'other', label: t('Other') },
                      ]}
                      hideSelectedOptions={false}
                      controlShouldRenderValue={true}
                      name='sex'
                      id='sex-select'
                      inputId='sex-input'
                      menuPlacement='top'
                      placeholder={t('Select')}
                    />
                  )}
                />
                {(errors.sex?.message || (errors.sex as any)?.value?.message) && (
                  <Form.Text className='text-danger'>
                    {t((errors.sex as any)?.value?.message || errors.sex?.message)}
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            {isAdult && mode == 'add' && (
              <>
                <div className='form-row'>
                  <Form.Group className='form-group' controlId='formEmail'>
                    <Form.Label>{t('Email')}</Form.Label>
                    <Form.Control
                      type='email'
                      {...register('email', {
                        onChange: handleEmailChange,
                      })}
                    />
                    {errors.email?.message && (
                      <Form.Text className='text-danger'>{t(errors.email.message)}</Form.Text>
                    )}
                    {!checkingEmailAvailability && !emailAvailable && (
                      <Form.Text className='text-danger'>
                        {t('This Email is already registered.')}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className='form-group' controlId='formPhone'>
                    <Form.Label>{t('Phone')}</Form.Label>
                    <Form.Control
                      type='tel'
                      {...register('phone', {
                        onChange: handlePhoneChange,
                      })}
                    />
                    {errors.phone?.message && (
                      <Form.Text className='text-danger'>{t(errors.phone.message)}</Form.Text>
                    )}
                    {!checkingPhoneAvailability && !phoneAvailable && (
                      <Form.Text className='text-danger'>
                        {t('This Phone Number is already in use.')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>
              </>
            )}

            <div className='form-row'>
              <Form.Group className='form-group form-group--large' controlId='formStreet'>
                <Form.Label>{t('Street')}</Form.Label>
                <PlacesAutocomplete
                  autocompleteRef={autocompleteRef}
                  onPlaceSelected={handlePlaceSelected}
                  placeholder={t('Street')}
                  value={watch('street')}
                  onChange={(value) => {
                    setValue('street', value, { shouldValidate: true, shouldDirty: true });
                    // Only clear other fields if user is manually typing (not selecting from autocomplete)
                    // When a place is selected, handlePlaceSelected will populate these fields
                    // So we only clear if the value is being deleted or changed manually
                    if (!value || value.trim().length === 0) {
                      setValue('unit', '', { shouldValidate: true });
                      setValue('city', '', { shouldValidate: true });
                      setValue('state', { label: '', value: '' }, { shouldValidate: true });
                      setValue('zip', '', { shouldValidate: true });
                      setValue('country', { label: '', value: '' }, { shouldValidate: true });
                    }
                  }}
                />
                {errors.street?.message && (
                  <Form.Text className='text-danger'>{t(errors.street.message)}</Form.Text>
                )}
              </Form.Group>
              <Form.Group className='form-group' controlId='formUnit'>
                <Form.Label>{t('Unit')}</Form.Label>
                <Form.Control type='text' {...register('unit')} />
                {errors.unit?.message && (
                  <Form.Text className='text-danger'>{t(errors.unit.message)}</Form.Text>
                )}
              </Form.Group>
            </div>

            <div className='form-row'>
              <Form.Group className='form-group' controlId='formCity'>
                <Form.Label>{t('City')}</Form.Label>
                <Form.Control type='text' {...register('city')} />
                {errors.city?.message && (
                  <Form.Text className='text-danger'>{t(errors.city.message)}</Form.Text>
                )}
              </Form.Group>

              <Form.Group className='form-group' controlId='formZip'>
                <Form.Label>{t('Zip')}</Form.Label>
                <Form.Control type='text' {...register('zip')} />
                {errors.zip?.message && (
                  <Form.Text className='text-danger'>{t(errors.zip.message)}</Form.Text>
                )}
              </Form.Group>
            </div>

            <div className='form-row'>
              <Form.Group className='form-group' controlId='formState'>
                <Form.Label>{t('State')}</Form.Label>
                <Controller
                  name='state'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={states}
                      hideSelectedOptions={false}
                      controlShouldRenderValue={true}
                      name='state'
                      id='state-select'
                      inputId='state-input'
                      menuPlacement='top'
                      placeholder={t('Select')}
                    />
                  )}
                />
                {errors.state?.message && (
                  <Form.Text className='text-danger'>{t(errors.state.message)}</Form.Text>
                )}
              </Form.Group>

              <Form.Group className='form-group' controlId='formCountry'>
                <Form.Label>{t('Country')}</Form.Label>
                <Controller
                  name='country'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={countries}
                      hideSelectedOptions={false}
                      controlShouldRenderValue={true}
                      name='country'
                      id='country-select'
                      inputId='country-input'
                      menuPlacement='top'
                      placeholder={t('Select')}
                    />
                  )}
                />
                {errors.country?.message && (
                  <Form.Text className='text-danger'>{t(errors.country.message)}</Form.Text>
                )}
              </Form.Group>
            </div>

            <LoaderButton
              type='submit'
              disabled={
                (mode === 'add' &&
                  isAdult &&
                  ((!checkingEmailAvailability && !emailAvailable) ||
                    (!checkingPhoneAvailability && !phoneAvailable))) ||
                (mode === 'edit' && (!isDirty || !isChanged || !isValid))
              }
            >
              {mode === 'edit' ? t('Save') : isMinor ? t('Add Member') : t('Send Invitation')}
            </LoaderButton>
          </Form>
        </Modal.Body>
      </Modal>
      <ConfirmationModal
        showModal={showConfirmationModal}
        onHide={confirmCloseModal}
        onConfirm={() => setShowConfirmationModal(false)}
        title={t('Cancel')}
        bodyText={t('Would you like to save changes before you leave?')}
        confirmButtonText={t('Yes, stay')}
        cancelButtonText={t('No, leave')}
      />
    </>
  );
};

export default AddOrEditMemberModal;
